import { type MetaFunction } from '@remix-run/node'
import { type ReactNode } from 'react'
import { EyebrowLabel } from '#app/components/eyebrow-label.tsx'
import { MarketingHero } from './components/marketing-hero.tsx'

// NOTE: Drafted in-house. Have counsel review before public launch.

const SECTIONS = [
	{ id: 'agreement', title: 'Agreement to terms' },
	{ id: 'accounts', title: 'Accounts' },
	{ id: 'use', title: 'Acceptable use' },
	{ id: 'content', title: 'User content' },
	{ id: 'health', title: 'Health and safety' },
	{ id: 'subscriptions', title: 'Subscriptions and payment' },
	{ id: 'termination', title: 'Termination' },
	{ id: 'disclaimers', title: 'Disclaimers' },
	{ id: 'liability', title: 'Limitation of liability' },
	{ id: 'changes', title: 'Changes to terms' },
	{ id: 'contact', title: 'Contact' },
]

export const meta: MetaFunction = () => [
	{ title: 'Terms of Service — Momentum' },
	{ name: 'description', content: 'Terms of Service for Momentum.' },
]

function P({ children }: { children: ReactNode }) {
	return (
		<p className="mt-4 text-base leading-relaxed text-ink-fg-soft">
			{children}
		</p>
	)
}

function Ul({ children }: { children: ReactNode }) {
	return (
		<ul className="mt-4 list-disc space-y-2 pl-6 text-base leading-relaxed text-ink-fg-soft">
			{children}
		</ul>
	)
}

export default function TermsOfServiceRoute() {
	return (
		<>
			<MarketingHero
				eyebrow="Terms"
				heading="Terms of Service"
				subhead="Last updated: July 7, 2026"
				ctaLabel=""
				variant="compact"
			/>

			<section className="mx-auto w-full max-w-5xl px-6 py-16 md:px-10 md:py-20">
				<div className="grid gap-12 md:grid-cols-[14rem_1fr]">
					<aside className="md:sticky md:top-24 md:self-start">
						<EyebrowLabel>Contents</EyebrowLabel>
						<ul className="mt-6 space-y-2 text-sm">
							{SECTIONS.map((s) => (
								<li key={s.id}>
									<a
										href={`#${s.id}`}
										className="text-ink-fg-soft hover:text-ink-fg"
									>
										{s.title}
									</a>
								</li>
							))}
						</ul>
					</aside>

					<article className="max-w-[60ch]">
						<section id="agreement" className="mb-12">
							<h2 className="font-display text-display-3">
								Agreement to terms
							</h2>
							<P>
								These Terms of Service (the “Terms”) are an agreement between
								you and Momentum (“Momentum,” “we,” or “us”) that governs your
								use of the Momentum website, applications, and services
								(together, the “Service”). By creating an account or using the
								Service, you agree to these Terms and to our{' '}
								<a href="/privacy" className="underline hover:text-ink-fg">
									Privacy Policy
								</a>
								. If you do not agree, do not use the Service.
							</P>
							<P>
								You must be at least 18 years old, or the age of majority where
								you live, to use the Service. By using the Service, you
								represent that you meet this requirement.
							</P>
						</section>

						<section id="accounts" className="mb-12">
							<h2 className="font-display text-display-3">Accounts</h2>
							<P>
								To use most of the Service you need an account. You agree to
								provide accurate information when you sign up and to keep it up
								to date. You may sign in with a username and password or through
								a third-party provider such as GitHub or Google; your use of
								those providers is governed by their own terms.
							</P>
							<P>
								You are responsible for your account and everything that happens
								under it. Keep your password confidential, and enable two-factor
								authentication if you want additional protection. Accounts are
								personal: one account per person, and you may not share your
								account or use someone else’s. Tell us right away at{' '}
								<a
									href="mailto:john@momentum.fitness"
									className="underline hover:text-ink-fg"
								>
									john@momentum.fitness
								</a>{' '}
								if you suspect unauthorized access to your account.
							</P>
						</section>

						<section id="use" className="mb-12">
							<h2 className="font-display text-display-3">Acceptable use</h2>
							<P>
								Use the Service only for its intended purpose: tracking,
								planning, and sharing your training. You agree not to:
							</P>
							<Ul>
								<li>use the Service in violation of any law or these Terms;</li>
								<li>
									impersonate any person, or misrepresent your affiliation with
									a person or organization;
								</li>
								<li>
									harass, threaten, or abuse other members, including in groups
									and coaching relationships;
								</li>
								<li>
									access another member’s data without permission, or attempt to
									probe, breach, or circumvent security or access controls;
								</li>
								<li>
									scrape, harvest, or bulk-download content or data from the
									Service, or use automated tools to access it except through
									interfaces we provide;
								</li>
								<li>
									interfere with the operation of the Service, including by
									introducing malware or placing unreasonable load on our
									infrastructure; or
								</li>
								<li>
									resell or commercially exploit the Service without our written
									permission.
								</li>
							</Ul>
							<P>
								We may investigate violations and take any action we consider
								appropriate, including removing content and suspending or
								terminating accounts.
							</P>
						</section>

						<section id="content" className="mb-12">
							<h2 className="font-display text-display-3">User content</h2>
							<P>
								You own the content you submit to the Service — your workouts,
								logs, notes, photos, and profile information (“User Content”).
								You grant us a worldwide, non-exclusive, royalty-free license to
								host, store, reproduce, and display your User Content as needed
								to operate, improve, and provide the Service. This includes
								showing your User Content to people you choose to share it with,
								such as coaches you work with and groups you join.
							</P>
							<P>
								You are responsible for your User Content and must have the
								rights to anything you submit. We may remove content that
								violates these Terms or the law. If you delete content or your
								account, we will delete or de-identify your User Content within
								a reasonable time, except where we need to retain it to comply
								with legal obligations or resolve disputes.
							</P>
						</section>

						<section id="health" className="mb-12">
							<h2 className="font-display text-display-3">Health and safety</h2>
							<P>
								Momentum is a training tool, not a medical service. Nothing in
								the Service — including workout plans, exercise descriptions,
								analytics, achievement targets, and AI-generated suggestions —
								is medical advice, and it is provided for informational purposes
								only. Coaches you interact with through the Service are not our
								employees or agents, and we do not verify their qualifications.
							</P>
							<P>
								Consult a physician before starting any exercise program,
								especially if you have a medical condition, an injury, or are
								new to training. Physical exercise carries inherent risks,
								including serious injury. You are solely responsible for
								deciding whether an exercise, weight, or program is appropriate
								for you, and you assume all risks associated with your training.
								Stop exercising and seek medical attention if you experience
								pain, dizziness, or other warning signs.
							</P>
						</section>

						<section id="subscriptions" className="mb-12">
							<h2 className="font-display text-display-3">
								Subscriptions and payment
							</h2>
							<P>
								The Service is currently free to use. We may introduce paid
								plans or paid features in the future. If we do, we will give you
								advance notice, and no existing feature you use will convert to
								paid billing without your explicit consent — you will never be
								charged retroactively or automatically enrolled in a paid plan.
							</P>
							<P>
								If you later purchase a paid plan, the price, billing period,
								and renewal terms will be disclosed at the time of purchase.
								Paid plans will renew automatically until cancelled, and you
								will be able to cancel at any time, effective at the end of the
								current billing period.
							</P>
						</section>

						<section id="termination" className="mb-12">
							<h2 className="font-display text-display-3">Termination</h2>
							<P>
								You may stop using the Service and delete your account at any
								time from your account settings. We may suspend or terminate
								your access if you violate these Terms, if we are required to by
								law, or if we discontinue the Service; where practical, we will
								give you notice and a chance to export your data.
							</P>
							<P>
								On termination, your right to use the Service ends and we will
								delete or de-identify your personal data as described in our{' '}
								<a href="/privacy" className="underline hover:text-ink-fg">
									Privacy Policy
								</a>
								. Sections of these Terms that by their nature should survive
								termination — including disclaimers, limitation of liability,
								and the license needed to complete deletion — survive.
							</P>
						</section>

						<section id="disclaimers" className="mb-12">
							<h2 className="font-display text-display-3">Disclaimers</h2>
							<P>
								The Service is provided “as is” and “as available,” without
								warranties of any kind, whether express or implied, including
								implied warranties of merchantability, fitness for a particular
								purpose, and non-infringement. We do not warrant that the
								Service will be uninterrupted, error-free, or secure, that
								defects will be corrected, or that any data — including workout
								history and analytics — will be accurate or preserved. Back up
								anything you cannot afford to lose.
							</P>
						</section>

						<section id="liability" className="mb-12">
							<h2 className="font-display text-display-3">
								Limitation of liability
							</h2>
							<P>
								To the fullest extent permitted by law, Momentum will not be
								liable for any indirect, incidental, special, consequential, or
								punitive damages, or for any loss of data, profits, or goodwill,
								arising out of or relating to your use of the Service — this
								includes, without limitation, personal injury arising from your
								training decisions, as described in the Health and safety
								section.
							</P>
							<P>
								To the fullest extent permitted by law, our total liability for
								all claims relating to the Service is limited to the greater of
								one hundred U.S. dollars ($100) or the amount you paid us in the
								twelve months before the claim arose. Some jurisdictions do not
								allow certain limitations of liability, so parts of this section
								may not apply to you.
							</P>
						</section>

						<section id="changes" className="mb-12">
							<h2 className="font-display text-display-3">Changes to terms</h2>
							<P>
								We may update these Terms from time to time. If a change is
								material, we will notify you — for example by email or by a
								notice in the Service — before it takes effect. The “Last
								updated” date above reflects the current version. Your continued
								use of the Service after a change takes effect means you accept
								the updated Terms; if you do not agree, stop using the Service
								and delete your account.
							</P>
						</section>

						<section id="contact" className="mb-12">
							<h2 className="font-display text-display-3">Contact</h2>
							<P>
								Questions about these Terms? Contact us at{' '}
								<a
									href="mailto:john@momentum.fitness"
									className="underline hover:text-ink-fg"
								>
									john@momentum.fitness
								</a>
								.
							</P>
							<P>
								These Terms are governed by the laws of the State of Indiana,
								without regard to its conflict-of-laws rules, and any dispute
								relating to them or to the Service will be brought in the state
								or federal courts located in Indiana. If any provision of these
								Terms is found unenforceable, the rest remain in effect. These
								Terms, together with the Privacy Policy, are the entire
								agreement between you and Momentum regarding the Service.
							</P>
						</section>
					</article>
				</div>
			</section>
		</>
	)
}
